import React from 'react';
import { Scissors, Sparkles, Heart, Users, Phone, MessageCircle, Info } from 'lucide-react';
import { SALON, HAIR_SERVICES, NAIL_SERVICES, FACIAL_SERVICES, THREADING_SERVICES, GROOMING_SERVICES, whatsappLink, telLink } from '../data/content';

const PriceRow = ({ name, price, desc }) => (
  <div className="flex items-start justify-between gap-4 py-4 border-b border-gold/10 last:border-b-0">
    <div>
      <div className="font-medium text-neutral-900">{name}</div>
      {desc && <div className="text-sm text-neutral-500 mt-1">{desc}</div>}
    </div>
    {price && (
      <div className="font-serif text-lg font-semibold text-gold whitespace-nowrap">{price}</div>
    )}
  </div>
);

const ServiceCategory = ({ icon: Icon, title, items }) => (
  <div className="bg-white rounded-2xl p-7 md:p-9 border border-gold/15 shadow-sm">
    <div className="flex items-center gap-4 mb-2">
      <div className="w-12 h-12 rounded-xl bg-gold-soft flex items-center justify-center">
        <Icon className="w-6 h-6 text-gold" strokeWidth={1.5} />
      </div>
      <h2 className="font-serif text-3xl font-bold text-neutral-900">{title}</h2>
    </div>
    <div className="gold-divider mb-4" />
    <div className="divide-y divide-gold/10">
      {items.map((item) => (
        <PriceRow key={item.name} {...item} />
      ))}
    </div>
  </div>
);

const Services = () => {
  return (
    <div>
      {/* Page Hero */}
      <section className="bg-gradient-to-b from-gold-soft/40 to-cream py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center animate-fade-in-up">
          <span className="text-gold font-medium text-sm tracking-[0.25em] uppercase">Our Services</span>
          <h1 className="font-serif text-5xl md:text-6xl font-bold mt-4 text-neutral-900">
            Professional Salon Services
          </h1>
          <div className="gold-divider mx-auto mt-6 mb-8" />
          <p className="text-lg text-neutral-600 leading-relaxed max-w-2xl mx-auto">
            From precision haircuts to relaxing spa treatments &mdash; discover our full range of
            premium hair, skin, nail and grooming services at {SALON.name}.
          </p>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-5 md:px-8 space-y-8">
          <ServiceCategory icon={Scissors} title="Hair Services" items={HAIR_SERVICES} />
          <ServiceCategory icon={Sparkles} title="Nail Services" items={NAIL_SERVICES.map(({ name }) => ({ name }))} />
          <ServiceCategory icon={Heart} title="Facial & Skin Care" items={FACIAL_SERVICES} />
          <ServiceCategory icon={Users} title="Threading & Waxing" items={THREADING_SERVICES} />
          <ServiceCategory icon={Users} title="Grooming Services" items={GROOMING_SERVICES} />

          {/* Disclaimer */}
          <div className="bg-gold-soft/40 border border-gold/20 rounded-2xl p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
              <Info className="w-5 h-5 text-gold" />
            </div>
            <p className="text-sm text-neutral-700 leading-relaxed">
              *Prices may vary based on hair length and service requirements. Please call us for
              specific pricing on services that are not listed with a price above.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gold to-gold-dark text-white">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">Ready to Book?</h2>
          <p className="text-white/90 mb-8">
            Call us or message on WhatsApp to schedule your appointment
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={telLink} className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-gold-dark rounded-full font-semibold shadow-lg btn-transition hover:shadow-xl">
              <Phone className="w-5 h-5" /> Call {SALON.phone}
            </a>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-whatsapp text-white rounded-full font-semibold shadow-lg hover:bg-whatsapp-dark btn-transition">
              <MessageCircle className="w-5 h-5 fill-white" /> Book on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
